"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { User, Mail, Lock, Save } from "lucide-react"
import { AlertModal, useCustomModal } from "../Modal/modal"
import { API_BASE_URL, fetchAuth, getCurrentUser, isCliente, setSession, getToken } from "../auth/auth"
import "./MiPerfil.css"

/**
 * Página "Mi perfil" — el cliente edita sus datos.
 *
 * El flujo:
 *   - Si cambia solo nombre/apellido/genero → no pide password actual.
 *   - Si cambia email o password → pide password actual.
 *   - PUT /edp/me con los campos modificados.
 *   - Al éxito, actualizamos localStorage.user (vía setSession) para
 *     que el Navbar refleje el nuevo nombre/email.
 */
const MiPerfil = () => {
  const navigate = useNavigate()
  const userInicial = getCurrentUser()

  const [form, setForm] = useState({
    nombre: userInicial?.nombre ?? "",
    apellido: userInicial?.apellido ?? "",
    email: userInicial?.email ?? "",
    genero: userInicial?.genero ?? "",
    password: "",
    password_confirmation: "",
    password_actual: "",
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  const { alertModal, showAlert, closeAlert } = useCustomModal()

  // ----- Guards -----
  if (!userInicial || !isCliente()) {
    return (
      <div className="mp-container">
        <div className="mp-card mp-empty">
          <h2>Tenés que iniciar sesión</h2>
          <p>Para ver y editar tu perfil necesitás una cuenta de cliente.</p>
          <button className="mp-btn-primary" onClick={() => navigate("/authForm")}>
            Iniciar sesión
          </button>
        </div>
      </div>
    )
  }

  // ----- Handlers -----
  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: "" }))
    }
  }

  // ¿Está intentando cambiar algo sensible? Eso define si pedimos
  // password_actual al backend.
  const emailCambio = form.email !== userInicial.email
  const passwordCambio = !!form.password
  const requierePasswordActual = emailCambio || passwordCambio

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    // Validación cliente mínima (la fuerte la hace el backend).
    if (passwordCambio && form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: "Las contraseñas no coinciden." })
      return
    }
    if (requierePasswordActual && !form.password_actual) {
      setErrors({ password_actual: "Para cambiar email o contraseña, ingresá tu contraseña actual." })
      return
    }

    // Armar payload SOLO con los campos modificados. Mandar todo no
    // está mal, pero queda más limpio en logs y evita validaciones
    // de unique sobre el propio email si no cambió.
    const payload = {}
    if (form.nombre !== userInicial.nombre) payload.nombre = form.nombre
    if (form.apellido !== userInicial.apellido) payload.apellido = form.apellido
    if (form.genero !== (userInicial.genero ?? "")) payload.genero = form.genero || null
    if (emailCambio) payload.email = form.email
    if (passwordCambio) {
      payload.password = form.password
      payload.password_confirmation = form.password_confirmation
    }
    if (requierePasswordActual) payload.password_actual = form.password_actual

    if (Object.keys(payload).length === 0) {
      await showAlert("Sin cambios", "No modificaste ningún dato.")
      return
    }

    setLoading(true)
    try {
      const res = await fetchAuth(`${API_BASE_URL}/me`, {
        method: "PUT",
        body: JSON.stringify(payload),
      })
      const data = await res.json()

      if (!res.ok) {
        if (data.errors) {
          // Validation errors del backend → inline en cada campo.
          const mapped = {}
          Object.entries(data.errors).forEach(([k, v]) => {
            mapped[k] = Array.isArray(v) ? v[0] : String(v)
          })
          setErrors(mapped)
        } else {
          await showAlert("No se pudo actualizar", data.message || `Error ${res.status}`)
        }
        return
      }

      // Éxito: refrescamos la sesión local con los datos nuevos. El
      // token sigue siendo el mismo, solo cambia el "user" del localStorage.
      setSession(getToken(), data.usuario)

      // Limpiamos los password fields del form.
      setForm((prev) => ({
        ...prev,
        password: "",
        password_confirmation: "",
        password_actual: "",
      }))

      await showAlert("Perfil actualizado", "Los cambios se guardaron correctamente.")
    } catch (err) {
      console.error(err)
      await showAlert("Error de conexión", "No se pudo contactar al servidor. Intentá de nuevo.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mp-container">
      <div className="mp-header">
        <h1 className="mp-title">Mi perfil</h1>
        <p className="mp-subtitle">Editá tus datos personales y de cuenta.</p>
      </div>

      <form onSubmit={handleSubmit} className="mp-form">
        {/* Bloque 1: datos personales */}
        <section className="mp-card">
          <h2 className="mp-section-title"><User size={18}/> Datos personales</h2>

          <div className="mp-row">
            <div className="mp-field">
              <label htmlFor="nombre">Nombre</label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                className={errors.nombre ? "mp-input mp-input-error" : "mp-input"}
                disabled={loading}
                required
              />
              {errors.nombre && <span className="mp-error">{errors.nombre}</span>}
            </div>

            <div className="mp-field">
              <label htmlFor="apellido">Apellido</label>
              <input
                type="text"
                id="apellido"
                name="apellido"
                value={form.apellido}
                onChange={handleChange}
                className={errors.apellido ? "mp-input mp-input-error" : "mp-input"}
                disabled={loading}
                required
              />
              {errors.apellido && <span className="mp-error">{errors.apellido}</span>}
            </div>
          </div>

          <div className="mp-field">
            <label htmlFor="genero">Género (opcional)</label>
            <select
              id="genero"
              name="genero"
              value={form.genero}
              onChange={handleChange}
              className={errors.genero ? "mp-input mp-input-error" : "mp-input"}
              disabled={loading}
            >
              <option value="">No especificar</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="O">Otro</option>
            </select>
            {errors.genero && <span className="mp-error">{errors.genero}</span>}
          </div>
        </section>

        {/* Bloque 2: cuenta */}
        <section className="mp-card">
          <h2 className="mp-section-title"><Mail size={18}/> Cuenta</h2>

          <div className="mp-field">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={errors.email ? "mp-input mp-input-error" : "mp-input"}
              disabled={loading}
              required
            />
            {errors.email && <span className="mp-error">{errors.email}</span>}
          </div>

          <h3 className="mp-subsection-title"><Lock size={16}/> Cambiar contraseña</h3>
          <p className="mp-hint">Dejá en blanco si no querés cambiarla.</p>

          <div className="mp-row">
            <div className="mp-field">
              <label htmlFor="password">Nueva contraseña</label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className={errors.password ? "mp-input mp-input-error" : "mp-input"}
                disabled={loading}
                autoComplete="new-password"
              />
              {errors.password && <span className="mp-error">{errors.password}</span>}
            </div>

            <div className="mp-field">
              <label htmlFor="password_confirmation">Confirmar nueva contraseña</label>
              <input
                type="password"
                id="password_confirmation"
                name="password_confirmation"
                value={form.password_confirmation}
                onChange={handleChange}
                className={errors.password_confirmation ? "mp-input mp-input-error" : "mp-input"}
                disabled={loading}
                autoComplete="new-password"
              />
              {errors.password_confirmation && <span className="mp-error">{errors.password_confirmation}</span>}
            </div>
          </div>

          {requierePasswordActual && (
            <div className="mp-field mp-current-password">
              <label htmlFor="password_actual">
                Contraseña actual <span className="mp-required">*</span>
              </label>
              <input
                type="password"
                id="password_actual"
                name="password_actual"
                value={form.password_actual}
                onChange={handleChange}
                className={errors.password_actual ? "mp-input mp-input-error" : "mp-input"}
                disabled={loading}
                autoComplete="current-password"
                required
              />
              <small className="mp-hint">
                Necesaria para confirmar que sos vos antes de cambiar email o contraseña.
              </small>
              {errors.password_actual && <span className="mp-error">{errors.password_actual}</span>}
            </div>
          )}
        </section>

        <div className="mp-actions">
          <button type="button" className="mp-btn-secondary" onClick={() => navigate("/")} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="mp-btn-primary" disabled={loading}>
            <Save size={16} /> {loading ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </form>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </div>
  )
}

export default MiPerfil
